import React from 'react';

import { formatAccountNumber } from '@eika-infrastruktur/format';
import { RowButton } from '@eika/button';
import { formatCurrencyValue } from '@eika/currency-input';
import { Icon, IconTypes } from '@eika/icon';
import { Label } from '@eika/label';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import { useFormContext } from '../../../../hooks/useForm';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { AnalyticsEvents, trackEvent } from '../../../../services/analytics';
import { formatKid } from '../../../../services/kidNumberService';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { NoteTypes } from '../../../../translations/translationType';
import sortAccounts from '../../../../utils/mapper/accountSorter';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { EfakturaLink } from '../../../EfakturaLink';
import { NoteField } from '../../../NoteField';
import { DateField } from '../../../shared/DateField';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import FieldSkeleton from '../../FieldSkeleton';
import { PaymentError } from '../../Payment/form/validate';
import { PaymentAction, PaymentActionType } from '../form/actions';
import { EfakturaState } from '../form/state';
import { amountLimitWarning, isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';

const EFakturaMobileFields: React.FC<FieldProps> = ({
    showMinAmount,
    locked,
    eFaktura,
    accounts,
    invalidAccounts,
    accountState,
    emptyFieldWarningLabel,
    bottomLabel,
    waitingForReceiver,
    kidLoading,
    date,
    paymentLimits,
    messageAllowed,
}) => {
    const { signingOperationPending } = useSigningContext();
    const { minDate, hasChangedDate, setChangedDate } = date;
    const { targetFromAccount } = usePaymentAccountContext();
    const translated = useTranslation();
    const { state, errors, handleChange } = useFormContext<EfakturaState, PaymentAction, PaymentError>();
    return (
        <FormFieldset disabled={signingOperationPending}>
            <AmountBox
                className="e-faktura-fields__amount-box"
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                error={errors.amount}
                limitError={errors.amountLimit}
                limitWarning={amountLimitWarning(translated, state, paymentLimits, targetFromAccount)}
                label={
                    showMinAmount && (
                        <Label text={format(translated.minAmountToPay, formatCurrencyValue(state.minimumAmount!))} />
                    )
                }
            >
                <AmountInput
                    value={state.amount}
                    ariaLabel={translated.payAmount}
                    onChange={(value) => {
                        handleChange({ type: PaymentActionType.AMOUNT, payload: value });
                        trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'set_amount');
                    }}
                    locked={locked}
                />
            </AmountBox>
            <div className="e-faktura-fields__pdf-link">
                <EfakturaLink eFakturaId={eFaktura.id} loading={eFaktura.loading} />
            </div>
            <SelectList>
                <li>
                    <AccountField
                        id="paymentFromAccount"
                        placeholder={translated.payFromAccount}
                        buttonLabel={translated.from}
                        title={accounts?.length ? translated.paymentFrom : ''}
                        account={targetFromAccount}
                        invalidAccounts={invalidAccounts}
                        accountNumber={state.fromAccount}
                        accounts={sortAccounts(accounts ?? [])}
                        customLabel={emptyFieldWarningLabel}
                        error={errors.accountId}
                        onChange={(accountNumber) => {
                            handleChange({
                                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                                payload: accountNumber,
                            });
                            trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'select_from_account');
                        }}
                        disabled={signingOperationPending}
                        isLoading={accountState.isLoading}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            handleChange({
                                type: showModal
                                    ? PaymentActionType.SHOW_FROM_ACCOUNTS
                                    : PaymentActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        locked={locked}
                        bottomLabel={bottomLabel}
                    />
                </li>
                <li>
                    {waitingForReceiver ? (
                        <FieldSkeleton />
                    ) : (
                        <RowButton disabled>
                            <RowButton.AccountSelectorLayout
                                label={translated.to}
                                accountAlias={state.toAccount?.accountName}
                                accountNumber={formatAccountNumber(state.toAccount?.accountNumber)}
                                icon={<Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" />}
                            />
                        </RowButton>
                    )}
                </li>
                <li>
                    {kidLoading ? (
                        <FieldSkeleton />
                    ) : (
                        <NoteField
                            placeholder=""
                            labelText={
                                messageAllowed ? translated.amountNote[NoteTypes.MESSAGE].label : translated.kidNumber
                            }
                            forceDisableKid={(state.toAccount?.kidFormats?.length ?? []) === 0 || !state.hasInitialKid}
                            kid={formatKid(state.kid)}
                            note={state.note}
                            onChange={(note) =>
                                handleChange({
                                    type: PaymentActionType.NOTE,
                                    payload: note,
                                })
                            }
                            locked={state.hasInitialKid}
                            selectedReceiver={state.toAccount}
                            labelAlwaysOn
                            error={errors.kid}
                        />
                    )}
                </li>
                <li>
                    <DateField
                        label={translated.date}
                        minDate={minDate}
                        date={state.paymentDate}
                        warning={hasChangedDate ? translated.eFaktura.eFakturaChangedDate : undefined}
                        onChange={(date) => {
                            setChangedDate(false);
                            handleChange({
                                type: PaymentActionType.PAYMENT_DATE,
                                // https://jirasd.intra.eika.no/browse/NMBP-802
                                // pga. endring fra button til tekstfelt og hvis bruker manuelt oppgir
                                // ugyldig dato, blir "date" undefined.
                                // eslint-disable-next-line
                                // @ts-ignore
                                payload: date,
                            });
                            trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'select_date');
                        }}
                        locked={locked}
                        disabled={signingOperationPending}
                        onlyBusinessDays
                    />
                </li>
            </SelectList>
        </FormFieldset>
    );
};

export default EFakturaMobileFields;
